/* eslint-disable import/prefer-default-export */
import { defineStore } from 'pinia';
import { ref } from 'vue';
import { useMutation, useMutationCache, useQuery } from '@pinia/colada';
import TrustDepositService from '@/services/accounting/TrustDepositService';
import type {
  AccountingMethod,
  DepositLevel,
  TrustDepositBody,
} from '@/models/trustDepositTypes';

/**
 * @pinia/colada is an internal request wrapper here — TrustDepositModal never imports it, and the
 * store's own refs stay the public contract.
 *
 * Payment methods are fetched fresh each time the dialog opens, as they were before this store
 * existed: `staleTime: 0` driven by `.refetch(true)`, where `true` is throwOnError — the default
 * resolves a failed fetch with the last-known state and the caller would never see the failure.
 * On failure the previous list is kept. The key is static, so the token guard stops a superseded
 * load (whose aborted query resolves with the entry's current state) from writing over a newer one.
 */
export const useTrustDepositStore = defineStore('trustDeposit', () => {
  const paymentMethods = ref<AccountingMethod[]>([]);
  const methodsLoading = ref(false);
  const methodsError = ref<unknown>(null);

  const methodsQuery = useQuery({
    key: () => ['accounting', 'methods'],
    query: () => TrustDepositService.fetchAccountingMethods(),
    enabled: false,
    staleTime: 0,
  });

  let loadToken = 0;

  async function loadPaymentMethods(): Promise<void> {
    const token = ++loadToken;
    methodsLoading.value = true;
    methodsError.value = null;
    try {
      const state = await methodsQuery.refetch(true);
      if (token !== loadToken) return;
      paymentMethods.value = state.data ?? paymentMethods.value;
    } catch (e) {
      if (token !== loadToken) return;
      methodsError.value = e;
      throw e;
    } finally {
      if (token === loadToken) methodsLoading.value = false;
    }
  }

  const depositMutation = useMutation({
    mutation: ({
      level,
      id,
      body,
    }: {
      level: DepositLevel;
      id: string;
      body: TrustDepositBody;
    }) => TrustDepositService.createDeposit(level, id, body),
  });

  const mutationCache = useMutationCache();

  async function recordDeposit(vars: {
    level: DepositLevel;
    id: string;
    body: TrustDepositBody;
  }) {
    try {
      return await depositMutation.mutateAsync(vars);
    } finally {
      // The store outlives the dialog, and Colada's cache keeps each run's input (amount,
      // source-of-funds memo) until GC. reset() alone only swaps the composable's entry.
      mutationCache
        .getEntries({ predicate: (e) => e.vars === vars })
        .forEach((e) => mutationCache.remove(e));
      depositMutation.reset();
    }
  }

  return {
    paymentMethods,
    methodsLoading,
    methodsError,
    loadPaymentMethods,
    recordDeposit,
  };
});
